import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Pagination helper
// ---------------------------------------------------------------------------

export interface Pagination {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
}

// ---------------------------------------------------------------------------
// Demand Forecasting types
// ---------------------------------------------------------------------------

export type ForecastTrend = "up" | "down" | "stable";

export interface ForecastProduct {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly brand: string;
  readonly current_stock: number;
  readonly forecast_7d: number;
  readonly forecast_30d: number;
  readonly forecast_90d: number;
  readonly reorder_point: number;
  readonly recommended_order_qty: number;
  readonly confidence_percent: number;
  readonly trend: ForecastTrend;
}

export interface ForecastListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly q?: string | null;
  readonly brand?: string | null;
}

export interface ForecastResponse {
  readonly products: readonly ForecastProduct[];
  readonly generated_at: string;
  readonly pagination: Pagination;
}

// ---------------------------------------------------------------------------
// Smart Pricing types
// ---------------------------------------------------------------------------

export interface PriceSuggestion {
  readonly product_id: string;
  readonly sku: string;
  readonly name: string;
  readonly brand: string;
  readonly current_price_fcfa: number;
  readonly suggested_price_fcfa: number;
  readonly price_delta_percent: number;
  readonly reason: string;
  readonly confidence_percent: number;
  readonly applied: boolean;
}

export interface SmartPricingResponse {
  readonly suggestions: readonly PriceSuggestion[];
  readonly potential_revenue_gain_fcfa: number;
}

// ---------------------------------------------------------------------------
// Churn Prediction types
// ---------------------------------------------------------------------------

export type ChurnRiskLevel = "low" | "medium" | "high" | "critical";

export interface ChurnRisk {
  readonly customer_id: string;
  readonly customer_name: string;
  readonly phone: string;
  readonly last_order_at: string;
  readonly days_since_order: number;
  readonly churn_probability_percent: number;
  readonly risk_level: ChurnRiskLevel;
  readonly recommended_action: string;
}

export interface ChurnListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly risk_level?: ChurnRiskLevel | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface ChurnResponse {
  readonly customers: readonly ChurnRisk[];
  readonly avg_churn_risk_percent: number;
  readonly high_risk_count: number;
  readonly pagination: Pagination;
}

// ---------------------------------------------------------------------------
// Anomaly Detection types
// ---------------------------------------------------------------------------

export type AnomalyType = "payment" | "order" | "inventory" | "agent";
export type AnomalySeverity = "low" | "medium" | "high" | "critical";
export type AnomalyStatus = "new" | "acknowledged" | "resolved";

export interface Anomaly {
  readonly id: string;
  readonly type: AnomalyType;
  readonly description: string;
  readonly severity: AnomalySeverity;
  readonly detected_at: string;
  readonly status: AnomalyStatus;
  readonly value: number;
  readonly expected_value: number;
  readonly deviation_percent: number;
}

export interface AnomalyListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly type?: AnomalyType | null;
  readonly severity?: AnomalySeverity | null;
  readonly status?: AnomalyStatus | null;
}

export interface AnomalyResponse {
  readonly anomalies: readonly Anomaly[];
  readonly new_count: number;
  readonly pagination: Pagination;
}

// ---------------------------------------------------------------------------
// Inventory Replenishment types
// ---------------------------------------------------------------------------

export type ReplenishmentUrgency = "ok" | "watch" | "order_soon" | "critical";

export interface ReplenishmentAlert {
  readonly product_id: string;
  readonly sku: string;
  readonly name: string;
  readonly brand: string;
  readonly current_stock: number;
  readonly avg_daily_sales: number;
  readonly days_of_stock_remaining: number;
  readonly urgency: ReplenishmentUrgency;
  readonly suggested_qty: number;
}

export interface ReplenishmentListParams {
  readonly urgency?: ReplenishmentUrgency | null;
}

export interface ReplenishmentResponse {
  readonly alerts: readonly ReplenishmentAlert[];
  readonly critical_count: number;
}

// ---------------------------------------------------------------------------
// Sales AI Insights types
// ---------------------------------------------------------------------------

export type InsightCategory = "opportunity" | "risk" | "trend";

export interface SalesInsight {
  readonly id: string;
  readonly category: InsightCategory;
  readonly title: string;
  readonly description: string;
  readonly impact_fcfa: number | null;
  readonly confidence_percent: number;
  readonly created_at: string;
}

export interface SalesInsightsResponse {
  readonly insights: readonly SalesInsight[];
  readonly generated_at: string;
}

// ---------------------------------------------------------------------------
// Product Recommendations Engine types
// ---------------------------------------------------------------------------

export type RecommendationType =
  | "frequently_bought_together"
  | "similar_products"
  | "trending"
  | "personalized";

export interface RecommendationConfig {
  readonly id: string;
  readonly type: RecommendationType;
  readonly is_active: boolean;
  readonly product_count: number;
  readonly last_updated_at: string;
}

export interface RecommendationPerformance {
  readonly type: string;
  readonly impressions: number;
  readonly clicks: number;
  readonly conversions: number;
  readonly revenue_fcfa: number;
}

export interface RecommendationsResponse {
  readonly configs: readonly RecommendationConfig[];
  readonly performance: readonly RecommendationPerformance[];
}

// ---------------------------------------------------------------------------
// Sentiment Analysis types
// ---------------------------------------------------------------------------

export type SentimentPeriod = "7d" | "30d" | "90d";

export interface SentimentSummary {
  readonly period: string;
  readonly avg_score: number;
  readonly positive_percent: number;
  readonly neutral_percent: number;
  readonly negative_percent: number;
  readonly total_reviews: number;
}

export type SentimentValue = "positive" | "neutral" | "negative";

export interface SentimentTopic {
  readonly topic: string;
  readonly mentions: number;
  readonly sentiment: SentimentValue;
}

export interface SentimentTrendPoint {
  readonly date: string;
  readonly score: number;
}

export interface SentimentResponse {
  readonly summary: SentimentSummary;
  readonly topics: readonly SentimentTopic[];
  readonly trend: readonly SentimentTrendPoint[];
}

// ---------------------------------------------------------------------------
// AI Dashboard KPIs
// ---------------------------------------------------------------------------

export interface AIDashboardKPIs {
  readonly data_quality_score: number;
  readonly forecast_accuracy_percent: number | null;
  readonly pricing_revenue_gain_fcfa: number;
  readonly churn_high_risk_count: number;
  readonly anomalies_detected: number;
  readonly avg_orders_per_day: number;
  readonly last_model_update: string;
  readonly active_models: number;
  readonly total_data_points: number;
}

// ---------------------------------------------------------------------------
// Model Status (from registry)
// ---------------------------------------------------------------------------

export interface ModelStatusEntry {
  readonly model_name: string;
  readonly display_name: string;
  readonly description: string;
  readonly algorithm: string;
  readonly status: "active" | "not_computed";
  readonly last_computed_at: string | null;
  readonly data_points_used: number;
  readonly backtested_accuracy_percent: number | null;
  readonly accuracy_metric: string | null;
}

export interface ModelStatusResponse {
  readonly models: readonly ModelStatusEntry[];
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const aiApi = {
  forecast: {
    list: (params: ForecastListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        q: params.q,
        brand: params.brand,
      });
      return adminFetch<ForecastResponse>(`/api/admin/ai/forecast${qs}`);
    },

    refresh: () =>
      adminFetch<{ generated_at: string }>("/api/admin/ai/forecast/refresh", {
        method: "POST",
      }),
  },

  pricing: {
    suggestions: () =>
      adminFetch<SmartPricingResponse>("/api/admin/ai/smart-pricing"),

    apply: (productId: string, price: number) =>
      adminFetch<{ suggestion: PriceSuggestion }>(
        "/api/admin/ai/smart-pricing/apply",
        {
          method: "POST",
          body: JSON.stringify({ product_id: productId, price }),
        },
      ),

    applyAll: () =>
      adminFetch<{ applied_count: number }>(
        "/api/admin/ai/smart-pricing/apply-all",
        { method: "POST" },
      ),
  },

  churn: {
    list: (params: ChurnListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        risk_level: params.risk_level,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<ChurnResponse>(`/api/admin/ai/churn${qs}`);
    },
  },

  anomalies: {
    list: (params: AnomalyListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        type: params.type,
        severity: params.severity,
        status: params.status,
      });
      return adminFetch<AnomalyResponse>(`/api/admin/ai/anomalies${qs}`);
    },

    acknowledge: (id: string) =>
      adminFetch<{ anomaly: Anomaly }>(
        `/api/admin/ai/anomalies/${encodeURIComponent(id)}/acknowledge`,
        { method: "PATCH" },
      ),

    resolve: (id: string) =>
      adminFetch<{ anomaly: Anomaly }>(
        `/api/admin/ai/anomalies/${encodeURIComponent(id)}/resolve`,
        { method: "PATCH" },
      ),
  },

  replenishment: {
    list: (params: ReplenishmentListParams = {}) => {
      const qs = toQueryString({ urgency: params.urgency });
      return adminFetch<ReplenishmentResponse>(
        `/api/admin/ai/replenishment${qs}`,
      );
    },
  },

  insights: {
    list: () =>
      adminFetch<SalesInsightsResponse>("/api/admin/ai/insights"),
  },

  recommendations: {
    get: () =>
      adminFetch<RecommendationsResponse>("/api/admin/ai/recommendations"),

    toggle: (id: string) =>
      adminFetch<{ config: RecommendationConfig }>(
        `/api/admin/ai/recommendations/${encodeURIComponent(id)}/toggle`,
        { method: "PATCH" },
      ),

    retrain: (id: string) =>
      adminFetch<{ config: RecommendationConfig }>(
        `/api/admin/ai/recommendations/${encodeURIComponent(id)}/retrain`,
        { method: "POST" },
      ),
  },

  sentiment: {
    get: (period: SentimentPeriod = "30d") => {
      const qs = toQueryString({ period });
      return adminFetch<SentimentResponse>(`/api/admin/ai/sentiment${qs}`);
    },
  },

  dashboard: {
    kpis: () =>
      adminFetch<AIDashboardKPIs>("/api/admin/ai/dashboard"),
  },

  models: {
    status: () =>
      adminFetch<ModelStatusResponse>("/api/admin/ai/models"),
  },
} as const;
